import { motion } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Cases() {
  const { t } = useLanguage();
  const items = t("cases.items");
  const navigate = useNavigate();

  return (
    <section id="cases" data-testid="cases-section" className="relative py-24 md:py-32 lg:py-48 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16 md:mb-20"
        >
          <span className="font-mono text-xs tracking-[0.25em] text-[#00F0FF] mb-4 block">
            {t("cases.overline")}
          </span>
          <h2 className="font-outfit font-bold text-4xl md:text-5xl leading-tight tracking-tighter text-white mb-4">
            {t("cases.title")}
          </h2>
          <p className="text-lg text-neutral-400 font-manrope max-w-2xl">
            {t("cases.subtitle")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {Array.isArray(items) && items.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              data-testid={`case-card-${i}`}
              onClick={() => item.slug && navigate(`/cases/${item.slug}`)}
              className="group bg-[#0a0a0a] border border-white/10 rounded-sm overflow-hidden hover:border-white/20 transition-all duration-500 cursor-pointer"
            >
              {item.image && (
                <div className="relative h-48 overflow-hidden">
                  <img src={item.image} alt={item.client} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/40 to-transparent" />
                  <div className="absolute bottom-4 left-6">
                    <span className="font-mono text-[10px] tracking-[0.15em] text-[#00F0FF]">{item.industry}</span>
                    <h3 className="font-outfit font-bold text-xl text-white">{item.client}</h3>
                  </div>
                </div>
              )}

              <div className="p-6 md:p-8">
                {!item.image && (
                  <>
                    <span className="font-mono text-xs tracking-[0.15em] text-[#00F0FF] mb-4 block">{item.industry}</span>
                    <h3 className="font-outfit font-bold text-2xl text-white mb-4">{item.client}</h3>
                  </>
                )}

                <div className="space-y-3 mb-5">
                  <div>
                    <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-600 block mb-1">CHALLENGE</span>
                    <p className="text-sm text-neutral-400 font-manrope leading-relaxed">{item.challenge}</p>
                  </div>
                  <div>
                    <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-600 block mb-1">SOLUTION</span>
                    <p className="text-sm text-neutral-400 font-manrope leading-relaxed">{item.solution}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  {item.features?.map((f, fi) => (
                    <span key={fi} className="text-[11px] font-mono tracking-wider text-neutral-500 bg-white/5 px-2.5 py-0.5 rounded-sm border border-white/5">
                      {f}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-5 border-t border-white/5">
                  <p className="text-sm text-[#00F0FF] font-manrope font-medium leading-snug max-w-[85%]">
                    {item.result}
                  </p>
                  <ArrowRight className="w-4 h-4 text-neutral-600 group-hover:text-[#00F0FF] group-hover:translate-x-1 transition-all duration-300 flex-shrink-0" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
